import { makeStyles } from '@material-ui/styles'
import ChangeOwnerDialog from 'app/plugins/openstack/components/vms/ChangeOwnerDialog'
import DeleteVirtualMachineDialog from 'app/plugins/openstack/components/vms/DeleteVirtualMachineDialog'
import { IVirtualMachinesListPageTabs } from 'app/plugins/openstack/components/vms/VirtualMachinesListPage'
import { getHideCondForDeleteOnTermination } from 'app/plugins/openstack/helpers'
import { RootState } from 'app/store'
import { ArrayElement } from 'core/actions/Action'
import { clientActions } from 'core/client/clientReducers'
import DocumentMeta from 'core/components/DocumentMeta'
import Button from 'core/elements/button/Button'
import { BatchActionDropdown } from 'core/elements/grid/GridToolbar'
import {
  GridBatchActionGroupProps,
  GridBatchActionSpec,
} from 'core/elements/grid/hooks/useGridSelectableRows'
import { HeaderPrimaryActionPortal, HeaderRefreshPortal } from 'core/elements/header/portals'
import Tabs from 'core/elements/tabs'
import Tab from 'core/elements/tabs/Tab'
import useListAction from 'core/hooks/useListAction'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import Theme from 'core/themes/model'
import { isReadonlyUser } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import { getSingleVolume } from 'openstack/components/storage/volumes/actions'
import { prop } from 'ramda'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import {
  getConsoleEndpoint,
  getVmEvents,
  listSingleVirtualMachine,
  updateSingleVm,
} from '../actions'
import AddFixedIpModal from '../AddFixedIpModal'
import AddFloatingIpDialog from '../AddFloatingIpDialog'
import AddServerGroupDialog from '../AddServerGroupDialog'
import CloneVirtualMachineModal from '../CloneVirtualMachineModal'
import ConfirmResizeDialog from '../ConfirmResizeDialog'
import DeleteOnTerminationDialog from '../DeleteOnTerminationDialog'
import EditVmMetadataModal from '../EditVmMetadataModal'
import EditVmSecurityGroupsModal from '../EditVmSecurityGroupsModal'
import GetDisabledTooltipForVMs, { canMigrateVMs } from '../GetDisabledTooltipForVMs'
import HotplugVmDialog from '../HotplugVmDialog'
import CustomVmLeaseModal from '../leases/CustomVmLeaseModal'
import ManageVolumesModal from '../ManageVolumesModal'
import MigrateVirtualMachineDialog from '../MigrateVirtualMachineDialog'
import { IVirtualMachinesSelector } from '../model'
import PowerVirtualMachineDialog from '../PowerVirtualMachineDialog'
import RebuildVirtualMachineDialog from '../RebuildVirtualMachineDialog'
import RemoveFixedIpDialog from '../RemoveFixedIpDialog'
import RemoveFloatingIpDialog from '../RemoveFloatingIpDialog'
import RemoveServerGroupDialog from '../RemoveServerGroupDialog'
import RenameVirtualMachineDialog from '../RenameVirtualMachineDialog'
import RescueVmDialog from '../RescueVmDialog'
import ResizeVirtualMachineDialog from '../ResizeVirtualMachineDialog'
import { singleVirtualMachineSelector, virtualMachinesSelector } from '../selectors'
import SnapshotVirtualMachineDialog from '../SnapshotVirtualMachineDialog'
import {
  ACTION_LIMITATION_TOOLTIPS,
  TOOLTIP_MESSAGES,
  VOLUME_BACKED_VM_ERROR_MSG,
} from '../tooltipConstants'
import UnrescueVmDialog from '../UnrescueVmDialog'
import UpdateMigrationPriorityDialog from '../UpdateMigrationPriorityDialog'
import VMResourceUtilizationDashboard from '../VMResourceUtilizationDashboard'
import GetDialogButton from './GetDailogButton'
import { IVmDetailsPageTabs } from './model'
import Overview from './Overview'
import { isVolumeBackedVm } from '../helpers'

const useStyles = makeStyles<Theme>((theme) => ({
  grid: {
    display: 'grid',
    gap: 32,
  },
  actions: {
    display: 'grid',
    gridAutoColumns: 'auto max-content',
    gridAutoFlow: 'column',
    gap: 16,
  },
  dropdownAction: {
    background: theme.components.dropdown.background,
    border: 'none',
    '& .button-text': {
      color: theme.components.dropdown.color,
      justifyContent: 'flex-start',
    },
  },
}))

type SelectorModel = ArrayElement<ReturnType<typeof virtualMachinesSelector>>

const VmDetailsPage = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { match, history } = useReactRouter()
  const { id } = match.params
  const session = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const userRole = session?.userDetails?.role
  const {
    prefs: { currentTenant },
  } = useScopedPreferences()

  const { loading, reload } = useListAction(listSingleVirtualMachine, { params: { id } })
  const { update, updating } = useUpdateAction(updateSingleVm)
  const data = useSelector(singleVirtualMachineSelector)
  const vm = useMemo(() => {
    return data[0]
  }, [data])

  const loadingProperVm = vm?.id !== id

  // As part of decoupling the single VM action from all VM action and desiring to
  // update the single VM without calling all the dependencies again, must call this
  // update upon loading the page
  useEffect(() => {
    update({ id })
  }, [id])

  useEffect(() => {
    dispatch(
      clientActions.updateBreadcrumbParams({
        id: vm?.name || id,
      }),
    )
    return () => {
      dispatch(clientActions.resetBreadcrumbParams())
    }
  }, [vm?.name, id])

  // Lifted tasks out of TasksPage so that refresh button
  // could refresh the tasks too
  const [loadingTasks, setLoadingTasks] = useState(false)
  const [reloadEvents, setReloadEvents] = useState(true)
  const [tasks, setTasks] = useState([])
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const getEvents = useCallback(async (id) => {
    setLoadingTasks(true)

    const events = await getVmEvents({ id })
    setTasks(events)
    setReloadEvents(false)
    setLoadingTasks(false)
  }, [])

  const [volumes, setVolumes] = useState([])
  const [loadingVolumes, setLoadingVolumes] = useState(true)

  const getVolumes = useCallback(async () => {
    if (!vm?.volumeIds?.length) {
      setLoadingVolumes(false)
      return
    }
    try {
      setLoadingVolumes(true)
      const promises = vm?.volumeIds?.map((id) => getSingleVolume(id))
      const responses = await Promise.allSettled(promises)
      const vols = responses
        .filter((response) => response.status === 'fulfilled')
        .map((response: any) => response.value)
      setVolumes(vols)
    } catch (e) {
      console.log(e)
    } finally {
      setLoadingVolumes(false)
    }
  }, [vm?.volumeIds])

  useEffect(() => {
    getVolumes()
  }, [getVolumes])

  useEffect(() => {
    if (!vm?.id) {
      return
    }
    getEvents(vm.id)
  }, [vm?.id])

  useEffect(() => {
    if (vm?.id && reloadEvents) {
      getEvents(vm.id)
    }
  }, [vm?.id, reloadEvents])

  const dropdownBatchActions: GridBatchActionGroupProps<SelectorModel>[] = useMemo(
    () => [
      {
        icon: '',
        label: 'Power Actions',
        actions: [
          {
            key: 'Start',
            label: 'Start',
            icon: 'circle-play',
            disabled: !['shutoff', 'stopped'].includes(vm?.state),
            tooltip: !['shutoff', 'stopped'].includes(vm?.state)
              ? ACTION_LIMITATION_TOOLTIPS.START
              : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              PowerVirtualMachineDialog,
              { action: 'start', rows: [vm] },
              { className: classes.dropdownAction },
            ),
          },
          {
            key: 'Stop',
            label: 'Stop',
            icon: 'stop',
            disabled: vm?.state !== 'active',
            tooltip: vm?.state !== 'active' ? ACTION_LIMITATION_TOOLTIPS.STOP : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              PowerVirtualMachineDialog,
              { action: 'stop', rows: [vm] },
              { className: classes.dropdownAction },
            ),
          },
          {
            key: 'Reboot',
            label: 'Reboot',
            icon: 'repeat',
            disabled: vm?.state !== 'active',
            tooltip: vm?.state !== 'active' ? ACTION_LIMITATION_TOOLTIPS.REBOOT : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              PowerVirtualMachineDialog,
              { action: 'reboot', type: 'SOFT', rows: [vm] },
              { className: classes.dropdownAction },
            ),
          },
          {
            key: 'HardReboot',
            label: 'Hard Reboot',
            icon: 'rotate-exclamation',
            disabled: vm?.state !== 'active',
            tooltip: vm?.state !== 'active' ? ACTION_LIMITATION_TOOLTIPS.REBOOT : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              PowerVirtualMachineDialog,
              { action: 'reboot', type: 'HARD', rows: [vm] },
              { className: classes.dropdownAction },
            ),
          },
          {
            key: 'Suspend',
            label: 'Suspend',
            icon: 'pause',
            disabled: vm?.state !== 'active',
            tooltip: vm?.state !== 'active' ? ACTION_LIMITATION_TOOLTIPS.SUSPEND : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              PowerVirtualMachineDialog,
              { action: 'suspend', rows: [vm] },
              { className: classes.dropdownAction },
            ),
          },
          {
            key: 'Pause',
            label: 'Pause',
            icon: 'circle-pause',
            disabled: vm?.state !== 'active',
            tooltip: vm?.state !== 'active' ? ACTION_LIMITATION_TOOLTIPS.PAUSE : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              PowerVirtualMachineDialog,
              { action: 'pause', rows: [vm] },
              { className: classes.dropdownAction },
            ),
          },
          {
            key: 'Unpause',
            label: 'Unpause',
            icon: 'circle-play',
            disabled: vm?.state !== 'paused',
            tooltip: vm?.state !== 'paused' ? ACTION_LIMITATION_TOOLTIPS.UNPAUSE : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              PowerVirtualMachineDialog,
              { action: 'unpause', rows: [vm] },
              { className: classes.dropdownAction },
            ),
          },
          {
            key: 'Resume',
            label: 'Resume',
            icon: 'play',
            disabled: vm?.state !== 'suspended',
            tooltip: vm?.state !== 'suspended' ? ACTION_LIMITATION_TOOLTIPS.RESUME : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              PowerVirtualMachineDialog,
              { action: 'resume', rows: [vm] },
              { className: classes.dropdownAction },
            ),
          },
          {
            key: 'Rescue',
            label: 'Rescue',
            icon: 'laptop-medical',
            disabled: vm?.state === 'rescued',
            tooltip: vm?.state === 'rescued' ? ACTION_LIMITATION_TOOLTIPS.ALREADY_RESCUED : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              RescueVmDialog,
              { rows: [vm] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'Unrescue',
            label: 'Unrescue',
            icon: 'laptop-binary',
            disabled: vm?.state !== 'rescued',
            tooltip: vm?.state !== 'rescued' ? ACTION_LIMITATION_TOOLTIPS.UNRESCUE : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              UnrescueVmDialog,
              { rows: [vm] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
        ],
      },
      {
        label: 'Network Actions',
        icon: '',
        actions: [
          {
            key: 'AddPrivateIP',
            label: 'Add Private IP',
            icon: 'square-plus',
            disabled: false,
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              AddFixedIpModal,
              { rows: [vm] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'RemovePrivateIP',
            label: 'Remove Private IP',
            icon: 'square-minus',
            disabled: false,
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              RemoveFixedIpDialog,
              { rows: [vm] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'AddPublicIP',
            label: 'Add Public IP',
            icon: 'square-plus',
            disabled: false,
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              AddFloatingIpDialog,
              { rows: [vm] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'RemovePublicIP',
            label: 'Remove Public IP',
            icon: 'square-minus',
            disabled: false,
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              RemoveFloatingIpDialog,
              { rows: [vm] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'EditSecurityGroups',
            label: 'Edit Security Groups',
            icon: 'lock',
            disabled: false,
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              EditVmSecurityGroupsModal,
              { rows: [vm] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
        ],
      },
      {
        label: 'Other Actions',
        icon: '',
        actions: [
          {
            key: 'Rename',
            label: 'Rename',
            icon: 'edit',
            disabled: false,
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              RenameVirtualMachineDialog,
              { rows: [vm] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'ChangeOwner',
            label: 'Change Owner',
            icon: 'user-edit',
            disabled: userRole !== 'admin',
            tooltip: userRole !== 'admin' ? 'Only administrators can change the owner of a VM' : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              ChangeOwnerDialog,
              { rows: [vm] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'Snapshot',
            label: 'Snapshot',
            icon: 'camera',
            disabled: vm?.flavor?.extra_specs?.['pci_passthrough:alias'],
            tooltip: vm?.flavor?.extra_specs?.['pci_passthrough:alias']
              ? TOOLTIP_MESSAGES.GPU_PASSTHROUGH_SNAPSHOT
              : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              SnapshotVirtualMachineDialog,
              { rows: [vm] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'Clone',
            label: 'Clone',
            icon: 'clone',
            disabled: false,
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              CloneVirtualMachineModal,
              { rows: [vm] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'ManageVolumes',
            label: 'Manage Volumes',
            icon: 'hard-drive',
            disabled:
              !['active', 'suspended', 'stopped'].includes(vm?.state) || !isVolumeBackedVm(vm),
            tooltip: !['active', 'suspended', 'stopped'].includes(vm?.state)
              ? ACTION_LIMITATION_TOOLTIPS.MANAGE_VOLUMES
              : !isVolumeBackedVm(vm)
              ? VOLUME_BACKED_VM_ERROR_MSG
              : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              ManageVolumesModal,
              { rows: [vm] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'EditMetadata',
            label: 'Edit Metadata',
            icon: 'tags',
            disabled: false,
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              EditVmMetadataModal,
              { rows: [vm] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'Resize',
            label: 'Resize',
            icon: 'up-right-and-down-left-from-center',
            disabled: vm?.state === 'resized',
            tooltip: vm?.state === 'resized' ? ACTION_LIMITATION_TOOLTIPS.ALREADY_RESIZED : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              ResizeVirtualMachineDialog,
              { rows: [vm] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'ConfirmResize',
            label: 'Confirm Resize',
            icon: 'box-check',
            disabled: vm?.state !== 'resized',
            tooltip: vm?.state !== 'resized' ? ACTION_LIMITATION_TOOLTIPS.NOT_RESIZED : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              ConfirmResizeDialog,
              { rows: [vm] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'Hotplug',
            label: 'Hotplug',
            icon: 'plug-circle-plus',
            disabled: !vm?.metadata?.['HOTPLUG_MEMORY'],
            tooltip: !vm?.metadata?.['HOTPLUG_MEMORY']
              ? ACTION_LIMITATION_TOOLTIPS.HOTPLUG_ENABLED
              : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              HotplugVmDialog,
              { rows: [vm] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'Migrate',
            label: 'Migrate',
            icon: 'database',
            disabled: !canMigrateVMs(userRole, [vm]),
            tooltip: !canMigrateVMs(userRole, [vm]) ? (
              <GetDisabledTooltipForVMs selected={[vm] as IVirtualMachinesSelector[]} />
            ) : (
              ''
            ),
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              MigrateVirtualMachineDialog,
              { rows: [vm] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'MigrationPriority',
            label: 'Migration Priority',
            icon: 'ranking-star',
            disabled: userRole !== 'admin',
            tooltip:
              userRole !== 'admin' ? ACTION_LIMITATION_TOOLTIPS.MIGRATION_PRIORITY_ADMIN_ONLY : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              UpdateMigrationPriorityDialog,
              { rows: [vm] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'Rebuild',
            label: 'Rebuild',
            icon: 'screwdriver-wrench',
            disabled: false,
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              RebuildVirtualMachineDialog,
              { rows: [vm] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'CustomizeLease',
            label: 'Customize Lease',
            icon: 'key',
            disabled: vm?.tenant_id !== currentTenant || !vm?.leasePolicy,
            tooltip:
              vm?.tenant_id !== currentTenant || !vm?.leasePolicy
                ? ACTION_LIMITATION_TOOLTIPS.TENANT_VM_LEASE_ONLY
                : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              CustomVmLeaseModal,
              { rows: [vm] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'EnableDeleteOnTermination',
            label: 'Enable Root Volume Delete on Termination',
            icon: 'trash',
            disabled:
              !['active', 'paused', 'resized'].includes(vm?.state) ||
              getHideCondForDeleteOnTermination([vm], true),
            hideIfDisabled: getHideCondForDeleteOnTermination([vm], true),
            tooltip: !['active', 'paused', 'resized'].includes(vm?.state)
              ? ACTION_LIMITATION_TOOLTIPS.DELETE_ON_TERMINATION_ENABLE
              : getHideCondForDeleteOnTermination([vm], true)
              ? ACTION_LIMITATION_TOOLTIPS.ALREADY_ENABLED
              : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              DeleteOnTerminationDialog,
              { rows: [vm] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'DisableDeleteOnTermination',
            label: 'Disable Root Volume Delete on Termination',
            icon: 'trash-slash',
            disabled:
              !['active', 'paused', 'resized'].includes(vm?.state) ||
              getHideCondForDeleteOnTermination([vm], false),
            hideIfDisabled: getHideCondForDeleteOnTermination([vm], false),
            tooltip: !['active', 'paused', 'resized'].includes(vm?.state)
              ? ACTION_LIMITATION_TOOLTIPS.DELETE_ON_TERMINATION_DISABLE
              : getHideCondForDeleteOnTermination([vm], false)
              ? ACTION_LIMITATION_TOOLTIPS.ALREADY_DISABLED
              : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              DeleteOnTerminationDialog,
              { rows: [vm] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'AddServerGroup',
            label: 'Add Server Group',
            icon: 'square-plus',
            disabled: Boolean(vm?.serverGroup) && ['active', 'stopped'].includes(vm?.state),
            tooltip:
              Boolean(vm?.serverGroup) && ['active', 'stopped'].includes(vm?.state)
                ? ACTION_LIMITATION_TOOLTIPS.VM_ALREADY_IN_SERVER_GROUP
                : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              AddServerGroupDialog,
              { rows: [vm] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            key: 'RemoveServerGroup',
            label: 'Remove Server Group',
            icon: 'square-minus',
            disabled: !Boolean(vm?.serverGroup) && ['active', 'stopped'].includes(vm?.state),
            tooltip:
              !Boolean(vm?.serverGroup) && ['active', 'stopped'].includes(vm?.state)
                ? 'The VM is not in any server group to remove. Please select a VM that is in a server group to remove.'
                : '',
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              RemoveServerGroupDialog,
              { rows: [vm] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
        ],
      },
    ],
    [classes, userRole, vm, currentTenant],
  )

  const batchActions: GridBatchActionSpec<SelectorModel>[] = [
    {
      cond: (vms) => {
        return vms?.length === 1
      },
      label: 'Console',
      icon: 'terminal',
      iconOnly: true,
      tooltip: TOOLTIP_MESSAGES.CONSOLE,
      handleAction: ([vm]) => {
        const openConsole = async () => {
          const url = await getConsoleEndpoint({ id: vm.id })
          const withVmName = `${url}&instance=${vm?.name || vm?.id}`
          window.open(withVmName)
        }
        openConsole()
      },
      refreshAfterSuccess: true,
    },
    {
      cond: (vms) => {
        return vms?.length === 1 && vms[0]?.state !== 'deleted' && vms[0]?.taskState !== 'deleting'
      },
      label: 'Delete',
      icon: 'trash-alt',
      iconOnly: true,
      tooltip: TOOLTIP_MESSAGES.DELETE,
      handleAction: () => {
        setShowDeleteDialog(true)
      },
      refreshAfterSuccess: true,
    },
  ]

  const handleOnDeleteSuccess = useCallback(() => {
    // After deletion of VM, redirect to VM list page
    history.push(
      routeManager.routes.openstack.vms.path({
        vmsTab: IVirtualMachinesListPageTabs.VirtualMachinesList,
      }),
    )
  }, [history])

  return (
    <>
      <DocumentMeta title="Virtual Machine Details" breadcrumbs />
      <HeaderRefreshPortal>
        <Button
          icon="refresh"
          variant="secondary"
          onClick={() => {
            reload(true)
            getEvents(vm.id)
            getVolumes()
          }}
          loading={loading || loadingProperVm || updating}
        >
          Refresh
        </Button>
      </HeaderRefreshPortal>
      {!isReadonlyUser() && (
        <HeaderPrimaryActionPortal>
          <>
            {batchActions.map((action) => (
              <Button
                key={action.label}
                icon={action.icon}
                variant="secondary"
                onClick={() => action.handleAction([vm])}
                disabled={action.cond ? !action.cond([vm]) : false}
              >
                {action.label}
              </Button>
            ))}
            {dropdownBatchActions.map(({ label, actions }) => (
              <BatchActionDropdown<SelectorModel>
                renderOutsideGrid
                icon=""
                key={label}
                label={label}
                actions={actions}
              />
            ))}
          </>
        </HeaderPrimaryActionPortal>
      )}
      {showDeleteDialog && (
        <DeleteVirtualMachineDialog
          onClose={() => setShowDeleteDialog(false)}
          rows={[vm]}
          onDeleteSuccess={handleOnDeleteSuccess}
        />
      )}
      <Tabs route={routeManager.routes.openstack.vmDetails}>
        <Tab label="Overview" value={IVmDetailsPageTabs.Overview}>
          <Overview
            vm={vm || {}}
            loading={loading || loadingProperVm || updating}
            tasks={tasks}
            loadingTasks={loadingTasks}
            volumes={volumes}
            loadingVolumes={loadingVolumes}
          />
        </Tab>
        <Tab label="Resource Utilization" value={IVmDetailsPageTabs.ResourceUtilization}>
          <VMResourceUtilizationDashboard resourceId={vm?.id} />
        </Tab>
      </Tabs>
    </>
  )
}

export default VmDetailsPage
